import type { ComponentProps } from 'react';

import { cn, getColoredCellStyle } from '../../lib/utils';
import { RatingToggle } from '../rating-toggle';

export interface RatingLevel {
  value: number;
  label: string;
  color: string;
}

type RatingSelectorProps = ComponentProps<'div'> & {
  columns: number;
};

function RatingSelector({
  columns,
  className,
  children,
  ...props
}: RatingSelectorProps) {
  return (
    <div
      data-slot={'rating-selector'}
      className={cn('grid w-full gap-2', className)}
      style={{
        gridTemplateColumns: `auto repeat(${columns}, 1fr)`,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

type RatingSelectorRowProps = Omit<ComponentProps<'div'>, 'onChange'> & {
  label: string;
  labelColor?: string;
  levels: RatingLevel[];
  value: number | null;
  onChange?: (value: number) => void;
  readOnly?: boolean;
};

function RatingSelectorRow({
  label,
  labelColor,
  levels,
  value,
  onChange,
  readOnly = false,
  className,
  ...props
}: RatingSelectorRowProps) {
  return (
    <div
      data-slot={'rating-selector-row'}
      role={'group'}
      aria-label={label}
      className={cn('contents', className)}
      {...props}
    >
      <RatingSelectorLabel label={label} color={labelColor} />
      {levels.map((level) => (
        <RatingToggle
          key={level.value}
          color={level.color}
          selected={value === level.value}
          readOnly={readOnly}
          onClick={readOnly ? undefined : () => onChange?.(level.value)}
          aria-label={`${level.label} (${level.value})`}
        >
          <RatingToggle.Label>{level.label}</RatingToggle.Label>
          <RatingToggle.Value>{level.value}</RatingToggle.Value>
        </RatingToggle>
      ))}
    </div>
  );
}

type RatingSelectorSpacerProps = ComponentProps<'div'>;

function RatingSelectorSpacer({
  className,
  ...props
}: RatingSelectorSpacerProps) {
  return (
    <div
      data-slot={'rating-selector-spacer'}
      className={cn('h-1', className)}
      style={{ gridColumn: '1 / -1' }}
      {...props}
    />
  );
}

type RatingSelectorLabelProps = ComponentProps<'div'> & {
  label: string;
  color?: string;
};

function RatingSelectorLabel({
  label,
  color,
  className,
  ...props
}: RatingSelectorLabelProps) {
  const bgColor = color ?? undefined;

  return (
    <div
      data-slot={'rating-selector-label'}
      className={cn(
        'flex items-center justify-center h-[46px] shrink-0 rounded-lg px-2.5',
        !bgColor && 'bg-primary-hover',
        className
      )}
      style={bgColor ? getColoredCellStyle(bgColor) : undefined}
      {...props}
    >
      <span
        className={cn(
          'text-base font-semibold text-center whitespace-nowrap truncate',
          !bgColor && 'text-primary-foreground'
        )}
      >
        {label}
      </span>
    </div>
  );
}

RatingSelector.Row = RatingSelectorRow;
RatingSelector.Spacer = RatingSelectorSpacer;
RatingSelector.Label = RatingSelectorLabel;

export { RatingSelector };
export type {
  RatingSelectorLabelProps,
  RatingSelectorProps,
  RatingSelectorRowProps,
  RatingSelectorSpacerProps,
};
